import { Badge, CardContent } from '@invent/wl-ui-kit-next/ui';

import { ParticipantAvatar } from '@/design';
import type {
  IAdminParticipantHub,
  IAdminParticipantProfile
} from '../../api/participant-contracts';
import { absent } from '../../components/account-facts';
import { Fact, Section } from '../../components/admin-page';
import { ParticipantLink } from '../../components/participant-link';
import { RegistryNumberChip } from '../../components/registry-number-chip';
import { UnderReviewChip } from '../../components/under-review-chip';
import { registryNumbers } from '../../registries';
import { DisclosureSummary, disclosureSummaryOf } from './disclosure-summary';
import { hubMembers, profileIsEmpty, profilePlace, surveysOf } from './household';
import { MembershipsFact } from './memberships';
import { RecordDates } from './record-dates';

/**
 * The household at a glance (R7): each member a compact card — its kind and name, where its
 * profile places it, its registry numbers and where each stands, its disclosures at a glance
 * (`disclosure-summary.tsx`, SR-216), the surveys it holds, whether it is under review (SR-222,
 * F9's R11), its record dates (`record-dates.tsx`, F11's Q1), and for a person the teams and
 * firms that name them (`memberships.tsx`, F7's R7 — the card's one read of its own, re-read on
 * `written`) — and a link re-opening the hub on it, which lands on this same household with that
 * member first.
 * The one opened is marked instead of linked.
 *
 * A household of one says so, in the words that fit why: no account acts for the participant,
 * or the accounts that do act for nobody else. Both are states, not a screen explained.
 *
 * On a record page (SR-391, `recordPage`) the section is the record's details alone — every
 * profile member survey-api filled (`ProfileDetails`), a team's or firm's contact, phone and
 * addresses among them (the ticket's AC1, AC2). Since SR-407 it draws no card: the name, kind,
 * numbers, disclosures, surveys and dates are the record card's above the tabs, the teams and
 * firms that name the record and the others on its login are Related records', and drawing them
 * again here was the duplicated data the client named on 8 Oct.
 */
export function OverviewSection({
  hub,
  anchor,
  search,
  written,
  recordPage = false
}: {
  hub: IAdminParticipantHub;
  anchor: string;
  /** A record page (SR-391): the opened record's profile details alone, no card (SR-407). */
  recordPage?: boolean;
  /** The search the hub was opened from, carried by every link to another member's hub. */
  search: string;
  /** The page's write cue — a person's teams and firms are read again on it. */
  written: number;
}) {
  const members = hubMembers(hub);

  // A record page's Overview is the record's details alone (SR-407): its name, kind, numbers,
  // disclosures, surveys and dates are the record card's, above the tabs, and a second card of
  // them here was the duplicated data the client named (SR-384, 8 Oct).
  if (recordPage) {
    const profile = hub.participant.profile;
    return (
      <Section title="Overview" anchor={anchor}>
        <CardContent className="text-sm" data-member={hub.participant.participantId}>
          {profileIsEmpty(profile) ? (
            <p>{absent('No questionnaire has named this participant yet.')}</p>
          ) : (
            <ProfileDetails profile={profile} />
          )}
        </CardContent>
      </Section>
    );
  }

  return (
    <Section title="Overview" anchor={anchor}>
      <CardContent className="grid gap-3">
        <ul aria-label="Household" className="grid gap-3 md:grid-cols-2">
          {members.map((member, index) => {
            const place = profilePlace(member.profile);
            const numbers = registryNumbers(member.identifiers);
            const surveys = surveysOf(hub.responses, member.participantId);
            const summary = disclosureSummaryOf(hub, member.participantId);
            return (
              <li
                key={member.participantId}
                data-member={member.participantId}
                className="grid content-start gap-2 rounded-md border border-border p-4 text-sm"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <ParticipantAvatar
                    photoUri={member.photoUri}
                    displayName={member.displayName}
                    decorative
                  />
                  <Badge variant="soft">{member.kind}</Badge>
                  {index === 0 ? (
                    <>
                      <span className="font-medium">{member.displayName}</span>
                      <Badge variant="outline">Opened</Badge>
                    </>
                  ) : (
                    <ParticipantLink participant={member} search={search} />
                  )}
                  <UnderReviewChip underReview={member.underReview} />
                </div>
                {profileIsEmpty(member.profile) ? (
                  <p>{absent('No questionnaire has named this participant yet.')}</p>
                ) : (
                  place && <p>{place}</p>
                )}
                {numbers.length === 0 ? (
                  <p>{absent('No registry number held.')}</p>
                ) : (
                  <ul aria-label="Registry numbers" className="flex flex-wrap gap-1.5">
                    {numbers.map((number) => (
                      <li key={`${number.idType} ${number.value}`}>
                        <RegistryNumberChip number={number} />
                      </li>
                    ))}
                  </ul>
                )}
                {summary !== undefined && (
                  <p>
                    <DisclosureSummary summary={summary} />
                  </p>
                )}
                <p>
                  {surveys.length === 0 ? absent('No response held.') : surveys.join(' · ')}
                </p>
                <RecordDates participant={member} responses={hub.responses} />
                {member.kind === 'person' && (
                  <MembershipsFact
                    participantId={member.participantId}
                    search={search}
                    written={written}
                  />
                )}
              </li>
            );
          })}
        </ul>
        {members.length === 1 && (
          <p className="text-sm">
            {absent(
              hub.participant.accounts.length === 0
                ? 'No account acts for this participant, so its household is itself alone.'
                : 'No account acting for this participant acts for anybody else.'
            )}
          </p>
        )}
      </CardContent>
    </Section>
  );
}

/** The profile's members in the words a reader knows them by, in the order a record is read. */
const PROFILE_DETAILS: [keyof IAdminParticipantProfile, string][] = [
  ['firstName', 'First name'],
  ['lastName', 'Last name'],
  ['preferredName', 'Preferred name'],
  ['teamName', 'Team'],
  ['firmName', 'Firm'],
  ['legalFirmName', 'Legal firm name'],
  ['contactName', 'Contact'],
  ['city', 'City'],
  ['state', 'State'],
  ['postalCode', 'Zip code'],
  ['officeEmail', 'Office email'],
  ['phone', 'Phone'],
  ['cellPhone', 'Cell phone'],
  ['altEmail', 'Alternative email'],
  ['ccEmail', 'CC email']
];

/**
 * A record's details on its record page: each profile member survey-api filled, as sent — the
 * projection of what its questionnaires answered, so nothing here is composed or checked. One
 * unfilled is left out rather than said, since the profile's own sentence covers a profile of
 * nulls, and a column of "Not answered" would bury the few that are.
 */
function ProfileDetails({ profile }: { profile: IAdminParticipantProfile }) {
  const filled = PROFILE_DETAILS.flatMap(([member, label]) => {
    const value = profile[member];
    // A member written as "" is unfilled too: a label over nothing would say less than leaving it.
    return typeof value === 'string' && value.trim() !== '' ? [{ member, label, value }] : [];
  });
  return (
    <div role="group" aria-label="Details" className="grid gap-2 sm:grid-cols-2">
      {filled.map(({ member, label, value }) => (
        <div key={member} data-profile={member}>
          <Fact label={label} value={value} />
        </div>
      ))}
    </div>
  );
}
